import { IPagingInfo } from './surveys';
import API from '../api/api';
import { ApiRoutes } from '../constans/api';
import { EReportType } from '../constans/report';
import { filterEmptyFilters } from '../helpers/helpers';

export interface IGroupOfReportsItem {
  surveyId: string;
  organization: string;
  surveyName: string;
  surveyType: string;
  surveyEndDate: string;
  reports?: IGetReportsRequestResponse;
}

export interface IGetGroupOfReportsResp {
  items: IGroupOfReportsItem[];
  pagingInfo: IPagingInfo;
}

export interface IReportsFilters {
  pageNo?: string | null;
  perPage?: string | null;
}

export const getGroupOfReportsRequest = async (
  params: IReportsFilters,
  signal: AbortSignal
): Promise<IGetGroupOfReportsResp> => {
  const { groupOfReports } = ApiRoutes;
  const { data } = await API.get(groupOfReports, { params, signal });
  return data;
};

export interface IReportFilters {
  pageNo?: string | null;
  perPage?: string | null;
  sortOrder?: string | null;
  search?: string | null;
  active?: string | null;
}

export interface IShareUser {
  id: number;
  name: string;
  email: string;
}

export interface IAccessList {
  count: number;
  users: IShareUser[];
}

export interface IResponsiveness {
  responseCount: number;
  participantCount: number;
  responseRate: number;
  representativeness: string;
}

export interface IItemReportResponse {
  id: string;
  group: string;
  type: string;
  accessList: IAccessList;
  responsiveness: IResponsiveness;
  is_anonymized: boolean;
  survey: { name: string };
  department: number;
  sub_reports: IItemReportResponse[] | null;
}

export interface IGetReportsRequestResponse {
  items: IItemReportResponse[];
  pagingInfo: IPagingInfo;
}

export const getReportsRequest = async (
  params: IReportFilters,
  surveyId: string,
  search: string
): Promise<IGetReportsRequestResponse> => {
  const searchParam = search ? { search } : {};
  const { reports } = ApiRoutes;
  const { active, ...rest } = params;
  const filters = { ...searchParam, ...rest, surveyId };
  const { data } = await API.get(`${reports}`, { params: filters });
  return data;
};

export interface IGetReportDetailsResp {
  id: string;
  survey: {
    creationDate: string;
    name: string;
    scopeSummary: {
      categoryCount: number;
      predictiveStateCount: number;
      topicCount: number;
    };
    type: string;
  };
  organization: string;
  group: string;
  type: EReportType;
  responsiveness: IResponsiveness;
  benchmark_department?: string;
  contains_open_questions_responses?: boolean;
}

export const getReportDetailsRequest = async (reportId: string): Promise<IGetReportDetailsResp> => {
  const { reports } = ApiRoutes;
  const { data } = await API.get(`${reports}${reportId}/`);
  return data;
};

export interface IGetSurveyResultsFilters {
  ln?: any;
}

export interface ITopicResults {
  code: string;
  topic: {
    code: string;
    title: string;
    description: string;
    exampleStatement: string;
  };
  score: number;
  teamDistribution: ITeamDistribution;
  scoreRating: 'weak' | 'normal' | 'strong';
  agreementLevel: 'disagreement' | 'low' | 'high' | 'moderate';
  benchmarks: IBenchmarks;
}

export interface ICategoryResult {
  code: string;
  category: {
    code: string;
    title: string;
    description: string;
  };
  score: number;
  scoreRating: 'weak' | 'normal' | 'strong';
  topicResults: ITopicResults[];
  teamDistribution?: ITeamDistribution;
  benchmarks: IBenchmarks;
  [key: string]: any;
}

export interface ITeamDistribution {
  weak: number;
  normal: number;
  strong: number;
}

export interface IBenchmarks {
  internal: { score: number };
  historical: {
    score: number;
    change: number;
    changeDirectionality?: 'positive' | 'negative';
  };
  survey: {
    endDate: string;
    type: 'ADAPT' | 'PULSE';
  };
}

export interface IPredicTopic {
  topic: {
    code: string;
    title: string;
    description: string;
    exampleStatement: string;
  };
  score: number;
  scoreRating: 'weak' | 'normal' | 'strong';
  teamDistribution?: ITeamDistribution;
  benchmarks?: IBenchmarks;
}

export interface IPredicStates {
  description: string;
  topicResults: IPredicTopic[];
}

export interface IPredictiveStatesResults {
  description?: string;
  positiveStates?: IPredicStates;
  negativeStates?: IPredicStates;
}

export interface ISegment {
  code: ECode;
  title: EGroup;
  description: string;
}

export interface IRange {
  min: number;
  max: number;
}

export interface ICount {
  current: number;
  expectedRange: IRange;
}

export interface IRating {
  grade: 'weak' | 'normal' | 'strong';
  description: string;
}

export interface IPRMCategoryResults {
  category: {
    code: string;
    title: string;
    description: string;
  };
  result: {
    score: number;
    rating: 'weak' | 'normal' | 'strong';
  };
}

export enum ECode {
  S1 = 'S1',
  S2 = 'S2',
  S3 = 'S3',
  S4 = 'S4',
  S5 = 'S5',
}

export enum EGroup {
  Engaged = 'Engaged',
  Normal = 'Normal',
  Tired = 'Tired',
  Unmotivated = 'Unmotivated',
  Exhausted = 'Exhausted',
}

interface IPRMSurvey {
  endDate: string;
  type: 'ADAPT' | 'PULSE';
  numResponses: number;
}

interface IPRMAnalysisResults {
  code: ECode;
  share: number;
  count: number;
}

export interface IPrmHistoricalBenchmark {
  prmAnalysisResults: IPRMAnalysisResults[];
  survey: IPRMSurvey;
}

export interface ISegmentResults {
  segment: ISegment;
  count: ICount;
  share: ICount;
  rating?: IRating;
  workEnvironment: {
    categoryResults: IPRMCategoryResults[];
  };
}

export interface IPrmAnalysisResults {
  description: string;
  historicalBenchmarks: IPrmHistoricalBenchmark[];
  segmentResults: ISegmentResults[];
}

export interface IKpi {
  kpi: number;
  rating: IRating;
  target?: number;
}

export interface ISurvey {
  type: 'ADAPT' | 'PULSE';
  endDate: string | Date;
  kpiType: string;
}

export interface IKPIAnalysisResult {
  score: number;
  rating: string;
}

export interface IHistoricalBenchmark {
  id: string;
  survey: ISurvey;
  kpiAnalysisResult: IKPIAnalysisResult;
}

export interface IKpiAnalysisResults {
  description: string;
  current: IKpi;
  historicalBenchmarks?: IHistoricalBenchmark[];
}

export interface IStrengthsAndWeaknessesTopic {
  topic: ITopicStat;
  result: {
    count: number;
    share: number;
    rating: 'weak' | 'normal' | 'strong';
  };
  benchmarks: IBenchmarks;
}

export interface IStrengthsTopics {
  description: string;
  strengthResults: IStrengthsAndWeaknessesTopic[];
}

export interface IWeaknessesTopics {
  description: string;
  weaknessResults: IStrengthsAndWeaknessesTopic[];
}

export interface IStrengthsAndWeaknessesData {
  strengths?: IStrengthsTopics;
  weaknesses?: IWeaknessesTopics;
}

export interface IWorkEnvironment {
  topAndBottomTopics: ITopAndBottomTopics;
  categoryResults: ICategoryResult[];
  strengthsAndWeaknesses: IStrengthsAndWeaknessesData;
  description: string;
}

export interface ITeamPerformersResults {
  team: {
    id: string;
    name: string;
    reportId: string;
  };
  result: {
    score: number;
    rating: 'weak' | 'normal' | 'strong';
  };
  benchmarks: IBenchmarks;
}

export interface ITeamsPerformers {
  description: string;
  results: ITeamPerformersResults[];
}

export interface ILeadersPerformersResults {
  leader: string;
  result: {
    score: number;
    rating: 'weak' | 'normal' | 'strong';
  };
  benchmarks: IBenchmarks;
}

export interface ILeadersPerformers {
  description: string;
  results: ILeadersPerformersResults[];
}

export interface ITopAndBottomPerformersResults {
  description: string;
  topTeams?: ITeamsPerformers;
  bottomTeams?: ITeamsPerformers;
  mostImprovedTeams?: ITeamsPerformers;
  topLeaders?: ILeadersPerformers;
  bottomLeaders?: ILeadersPerformers;
  mostImprovedLeaders?: ILeadersPerformers;
}

export interface IGetSurveyResultsResponse {
  workEnvironment: IWorkEnvironment;
  predictiveStatesResults: IPredictiveStatesResults;
  prmAnalysisResults: IPrmAnalysisResults;
  kpiAnalysisResults?: IKpiAnalysisResults;
  topAndBottomPerformers: ITopAndBottomPerformersResults;
  psychosocialRisk: IPsychosocialRisk;
  [key: string]: any;
}

export interface ITopicStat {
  code: string;
  title: string;
  category: string;
}

export interface ITopicResAr {
  topic: ITopicStat;
  rating: {
    grade: 'weak' | 'normal' | 'strong';
  };
  result: {
    score: number;
  };
  benchmarks: IBenchmarks;
}
export interface IStatTopicResults {
  description: string;
  topicResults: ITopicResAr[];
}
export interface ITopAndBottomTopics {
  topTopics: IStatTopicResults;
  bottomTopics: IStatTopicResults;
  mostImprovedTopics: IStatTopicResults;
}
export interface ITopicsStatistics {
  workEnvironment: IWorkEnvironment;
}

export interface IRisk {
  level: string;
  description: string;
}

export interface IResult {
  count: number;
  share: number;
}

export interface IPsychosocialRiskResults {
  risk: IRisk;
  result: IResult;
}
export interface IPsychosocialRisk {
  description: string;
  results: IPsychosocialRiskResults[];
}

export const getSurveyResultsRequest = async (
  reportId: string,
  filters: IGetSurveyResultsFilters
) => {
  const { reports, surveyResults } = ApiRoutes;
  const url = `${reports}${reportId}${surveyResults}`;
  const { data } = await API.get<IGetSurveyResultsResponse>(url);
  return data;
};

export const getEffectSurveyResultsRequest = async (reportId: string) => {
  const { reports, effectSurveyResults } = ApiRoutes;
  const url = `${reports}${reportId}${effectSurveyResults}`;
  const { data } = await API.get<IGetEffectReportDetailsResp>(url);
  return data;
};

export const getEffectOpenQuestionsRequest = async (reportId: string) => {
  const { reports, openQuestions } = ApiRoutes;
  const url = `${reports}${reportId}${openQuestions}`;
  const { data } = await API.get<IGetOpenQuestionsResp>(url);
  return data;
};

export interface IImprovementItem {
  title: string;
  checkQuestion: string;
  recommendations: string[];
}

export interface IRecommendationItem {
  category: {
    code: string;
    title: string;
  };
  areasCount: number;
  areasOfImprovement: IImprovementItem[];
}

export interface IGetRecommendationsRequest {
  high?: IRecommendationItem[];
  medium?: IRecommendationItem[];
  low?: IRecommendationItem[];
  other?: IRecommendationItem[];
}

export const getRecommendationsRequest = async (
  reportId: string
): Promise<IGetRecommendationsRequest> => {
  const { reports, recommendations } = ApiRoutes;
  const url = `${reports}${reportId}${recommendations}`;
  const { data } = await API.get(url);
  return data;
};

export const deleteReportingGroup = async (groupId: string, surveyId: string) => {
  const { surveyList, reportingGroups } = ApiRoutes;
  const url = `${surveyList}${surveyId}/${reportingGroups}${groupId}`;
  const { data } = await API.delete(url);
  return data;
};

export interface IPostReportingGroupBody {
  type: string;
  name: string;
}

export const postReportingGroup = async (surveyId: string, body: IPostReportingGroupBody) => {
  const { surveyList, reportingGroups } = ApiRoutes;
  const url = `${surveyList}${surveyId}/${reportingGroups}`;
  const { data } = await API.post(url, body);
  return data;
};

export interface IPatchReportingGroupBody {
  name: string;
}

export const patchReportingGroup = async (
  groupId: string,
  surveyId: string,
  body: IPatchReportingGroupBody
) => {
  const { surveyList, reportingGroups } = ApiRoutes;
  const url = `${surveyList}${surveyId}/${reportingGroups}${groupId}/`;
  const { data } = await API.patch(url, body);
  return data;
};

export const deleteReportingGroupMember = async (
  groupId: string,
  surveyId: string,
  memberId: string
) => {
  const { members, surveyList, reportingGroups } = ApiRoutes;
  const url = `${surveyList}${surveyId}/${reportingGroups}${groupId}/${members}${memberId}`;
  const { data } = await API.delete(url);
  return data;
};

export const getReportPresentationLink = async (
  reportId: string,
  language: string,
  dataFormat: string
) => {
  const { reports, downloadPresentation } = ApiRoutes;
  const url = `${reports}${reportId}/${downloadPresentation}`;
  const body = { language, dataFormat };
  const { data } = await API.post(url, body);
  return data;
};

export interface ISuplementaryResourceItem {
  title: string;
  downloadUrl: string;
}
export interface ISuplementaryResourceResp {
  items: ISuplementaryResourceItem[];
  pagingInfo: IPagingInfo;
}

export const getResourcesRequest = async (
  reportId: string | undefined,
  suppFilters: IReportsFilters | any
) => {
  const { reports, supplementaryResources } = ApiRoutes;
  const url = `${reports}${reportId}/${supplementaryResources}`;
  const params = filterEmptyFilters(suppFilters);
  const { data } = await API.get(url, { params });

  return data;
};

export interface HistoricalSurvey {
  end_date: string;
}

export interface Question {
  average_value: number;
  change: any;
  historical_value: any;
  rank: string;
  text: string;
  num_idk?: string;
  single_respondent_max: number | null;
  single_respondent_min: number | null;
}
// TODO: proper TS here
export interface Topic {
  average_value: number;
  change: any;
  historical_value: any;
  code: string;
  description: string;
  external_benchmark: any;
  label: string;
  organisation_value: number;
  questions: Question[];
  rank: string;
  single_respondent_max: number;
  single_respondent_min: number;
  super_manager_value: any;
  threshold_values: {
    high: number;
    low: number;
    medium: number;
  };
}

export interface StructuredAnalysisData {
  results: {
    input?: Analysis;
    processes?: Analysis;
    emergentStates?: Analysis;
    output?: Analysis;
  };
  insights: {
    fiveChallenges?: Analysis;
    clarity?: Analysis;
    quality?: Analysis;
    relationship?: Analysis;
    behaving?: Analysis;
    learning?: Analysis;
  };
  externalResults: {
    external?: Analysis;
  };
  topAndBottom: {
    top?: Analysis;
    bottom?: Analysis;
    improved?: Analysis;
  };
}

export interface Analysis {
  type:
    | 'EFFECT BEHAVING'
    | 'EFFECT CLARITY'
    | 'EFFECT EMERGENT STATES'
    | 'EFFECT EXTERNAL'
    | 'EFFECT FIVE CHALLENGES'
    | 'EFFECT INPUT'
    | 'EFFECT PROCESSES'
    | 'EFFECT QUALITY'
    | 'EFFECT RELATIONSHIP'
    | 'EFFECT OUTPUT'
    | 'EFFECT TOP TOPICS'
    | 'EFFECT BOTTOM TOPICS'
    | 'EFFECT MOST IMPROVED'
    | 'EFFECT LEARNING';
  description: string;
  included_topics: string[];
}

export interface IGetEffectReportDetailsResp {
  historical_survey: HistoricalSurvey;
  topics: Topic[];
  analysis: Analysis[];
}

export interface IToggleState {
  historical_result: boolean;
  agreement?: boolean;
  variation_between_respondents: boolean;
  organization_mean: boolean;
  external_benchmark: boolean;
  team_manager: boolean;
}

export interface OpenQuestionsAnalysis {
  type: 'EFFECT OPEN QUESTIONS' | 'EFFECT OPEN QUESTIONS EXTERNAL';
  description: string;
  included_topics: string[];
}

export interface OpenQuestionsFieldsType {
  code: string;
  responses: { value: string }[];
  text: string;
}

export interface IGetOpenQuestionsResp {
  external: OpenQuestionsFieldsType[];
  internal: OpenQuestionsFieldsType[];
  analysis: OpenQuestionsAnalysis[];
}
